import { remapAiTagIds, useSaveAiTags } from '@solinkcorp/ai-tags';
import { useLocale } from '@solinkcorp/locale';
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@solinkcorp/solink-ui';
import { useFormik } from 'formik';
import { castDraft } from 'immer';
import { useControllerState } from 'solox';
import { toast } from 'sonner';
import { type Controller } from '../../../Controller';
import { useUpdateServiceDisplayWidgets } from '../../../hooks/useServiceDisplayLayouts';
import { sdmCancelBtnStyles } from './LayoutSelectorStyles';

interface LayoutFormValues {
  layoutName: string;
}

export const LayoutDetailsStep = ({ controller }: { controller: Controller }) => {
  const locale = useLocale();
  const updateServiceDisplayWidgets = useUpdateServiceDisplayWidgets();
  const editingLayout = useControllerState(controller.state, (state) => state.editingLayout);
  const layouts = useControllerState(controller.state, (state) => state.layouts);
  const editingLayoutOrigin = useControllerState(
    controller.state,
    (state) => state.editingLayoutOrigin,
  );
  const saveAiTags = useSaveAiTags();

  const isNew = !editingLayout?.id || !layouts.find((l) => l.id === editingLayout.id);
  const isFromTemplate = isNew && editingLayoutOrigin === 'template';
  const isDuplicate = isNew && (editingLayout?.widgets?.length ?? 0) > 0 && !isFromTemplate;
  const showBackButton = isNew && (isFromTemplate || editingLayoutOrigin === undefined);

  const formik = useFormik<LayoutFormValues>({
    initialValues: {
      layoutName: editingLayout?.name || '',
    },
    enableReinitialize: true,
    validateOnMount: true,
    validate: (values) => {
      const errors: Partial<LayoutFormValues> = {};
      const trimmedName = values.layoutName.trim();

      if (!trimmedName) {
        errors.layoutName = locale.serviceDisplayMonitor.layoutNameRequired;
      } else if (trimmedName.length > 30) {
        errors.layoutName = locale.serviceDisplayMonitor.layoutNameMaxLength;
      } else {
        const isDuplicateName = layouts.some(
          (l) => l.name.toLowerCase() === trimmedName.toLowerCase() && l.id !== editingLayout?.id,
        );
        if (isDuplicateName) {
          errors.layoutName = locale.serviceDisplayMonitor.layoutNameDuplicate;
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      let widgets = editingLayout?.widgets ? castDraft(editingLayout.widgets) : [];
      const layoutName = values.layoutName.trim();
      const tagIds = widgets.flatMap((w) => w.tagIds);

      if (tagIds.length > 0) {
        try {
          const idRemap = await saveAiTags(tagIds);
          if (idRemap.size > 0) {
            widgets = widgets.map((w) => ({
              ...w,
              tagIds: remapAiTagIds(w.tagIds, idRemap),
            }));
          }
        } catch (error) {
          console.error('Error saving service display AI tags', error);
          toast.error(locale.serviceDisplayMonitor.failedToSaveLayoutAiTags, {
            position: 'bottom-center',
          });
          return;
        }
      }

      try {
        if (isNew) {
          controller.setSelectedLayoutName(layoutName);
          await updateServiceDisplayWidgets(widgets, undefined, layoutName);
        } else {
          await updateServiceDisplayWidgets(widgets, editingLayout?.id, layoutName);
        }
      } catch (error) {
        console.error('Failed to save layout', error);
        toast.error(locale.serviceDisplayMonitor.failedToSaveLayout, {
          position: 'bottom-center',
        });
        return;
      }

      controller.displayLayoutDialog(false, undefined);
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    controller.displayLayoutDialog(false, undefined);
  };

  const handleBack = () => {
    formik.resetForm();
    controller.backToTemplatePicker();
  };

  const titleText = () => {
    if (showBackButton) return locale.serviceDisplayMonitor.layoutDetails;
    if (isDuplicate) return locale.serviceDisplayMonitor.duplicateLayout;
    if (isNew) return locale.serviceDisplayMonitor.createLayout;
    return locale.serviceDisplayMonitor.editLayout;
  };

  const descriptionText = () => {
    if (isDuplicate) return locale.serviceDisplayMonitor.duplicateLayoutDescription;
    if (isNew) return locale.serviceDisplayMonitor.createLayoutDescription;
    return locale.serviceDisplayMonitor.editLayoutDescription;
  };

  const submitButtonText = () => {
    if (showBackButton) return locale.serviceDisplayMonitor.createNewLayout;
    if (isNew) return locale.general.create;
    return locale.general.update;
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogHeader>
        <DialogTitle>{titleText()}</DialogTitle>
        {!showBackButton && <DialogDescription>{descriptionText()}</DialogDescription>}
      </DialogHeader>
      <div className="grid gap-4 pb-2 pt-4">
        <div className="grid gap-2">
          {showBackButton && (
            <Label htmlFor="layoutName" className="text-text-primary">
              {locale.serviceDisplayMonitor.layoutNameLabel}
              <span className="text-text-error"> *</span>
            </Label>
          )}
          <Input
            id="layoutName"
            name="layoutName"
            placeholder={locale.serviceDisplayMonitor.enterLayoutName}
            data-testid="layout-name-input"
            maxLength={30}
            value={formik.values.layoutName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            aria-invalid={!!formik.errors.layoutName}
          />
          <div className="height-fit flex h-5 items-center justify-between">
            <p className="my-0 min-h-5 pl-2 text-sm text-text-error">
              {formik.errors.layoutName ?? ''}
            </p>
            <p
              className={`text-sm ${formik.values.layoutName.length >= 30 ? 'text-text-error' : 'text-muted-foreground'}`}
            >
              {formik.values.layoutName.length}/30
            </p>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button
          className={sdmCancelBtnStyles}
          onClick={showBackButton ? handleBack : handleCancel}
          type="button"
          variant="outline"
          data-testid="layout-details-cancel-btn"
        >
          {showBackButton ? locale.general.back : locale.general.cancel}
        </Button>
        <Button
          className="text-interaction-primary-content-default"
          variant="default"
          type="submit"
          disabled={!formik.isValid || !formik.values.layoutName.trim()}
          isLoading={formik.isSubmitting}
          data-testid="layout-details-submit-btn"
        >
          {submitButtonText()}
        </Button>
      </DialogFooter>
    </form>
  );
};
